import { Injectable, inject, Injector } from '@angular/core';
import { ListStoreService, DB_CONNECTION_CONFIGS, PouchDB } from '@luzula/luzula-lib';
import type { ListConfig, ListOverviewEntry } from '@luzula/luzula-lib';
import { DatabaseConfigService } from '../database-config.service';
import { migrateListsToListenDb, ListMigrationResult, LIST_MIGRATION_MARKER } from './listen-migration';
import { normalizePunktartList } from '../data-model';
import { deriveFeinbodenformen, toStoredFeinbodenformen } from './feinbodenform-derivation';
import { normalizeBodensteckbriefe } from './bodensteckbrief-normalize';

export type { ListMetadata, ListConfig, ListType, ListOverviewEntry, ListVersion } from '@luzula/luzula-lib';

export interface BodenartEntry {
  name: string;
  Lagerungsdichte: { gering: number; mittel: number; hoch: number };
  Humusgehalt: { h2: number; h3: number; h4: number; h5: number; h6: number };
}

export interface FeinbodenformEntry {
  FBF: string;
  FBF_display: string;
  name: string;
  HBF: string;
  HBF_lang?: string;
  /**
   * Substratgruppe (S, bS, lS, G, bG, …): abgeleitet aus dem Katalog, wo sie an
   * der Hauptbodenform steht. Read-only wie HBF/NKr, wird nicht gespeichert.
   * Manuelle Waisen (kein Katalog-Treffer) haben sie nicht.
   */
  STG?: string;
  NKr: string;
  Hw: string | null;
  zuordnung: Record<string, string>;
  Serie: string | null;
  Seite?: string;
}

/**
 * Voller SEA95-Bodensteckbrief, wie er aus dem D_Parser
 * (`/api/export/all.json` -> `_build_page_export`) konsolidiert geliefert wird.
 * Editor und Preview in der Listenverwaltung arbeiten direkt auf diesem
 * Schema - kein PDF/Bild, kein raw/LLM/Korrektur-Diff (Merge passiert bereits
 * in D_Parser).
 */
export interface BodensteckbriefSpalte {
  ebenen: string[];
  empty?: boolean;
}

export interface BodensteckbriefFbfRow {
  lokalname: string;
  abkuerzung?: string;
  footnote_ref?: string;
  nahrkraftstufe?: string;
  funftelstufe?: string;
  forstbetrieb?: string;
  gestrichen?: boolean;
  schraffuren?: Record<string, string>;
  [k: string]: any;
}

export interface BodensteckbriefSection {
  kind: 'key_value' | 'text' | 'list' | 'diagram' | string;
  title: string;
  content: any;
}

export interface BodensteckbriefEntry {
  /** Stabile ID, z.B. "<pdf>#<page>". Identitaet ueber Re-Imports hinweg. */
  id: string;
  /** Herkunft aus D_Parser fuer Nachvollziehbarkeit (optional). */
  pdf?: string;
  page?: number;
  hauptbodenform: { name?: string; abkuerzung?: string; substratgruppe?: string };
  spalten: Record<string, BodensteckbriefSpalte>;
  /** Explizite Reihenfolge der Schraffur-Spalten (vom Editor gesetzt). Wenn
   *  nicht vorhanden, faellt Editor/Preview auf die numerische ID-Sortierung
   *  + Heuristik zurueck. */
  schraf_col_order?: string[];
  feinbodenformen: BodensteckbriefFbfRow[];
  sections?: BodensteckbriefSection[];
  horizont_diagramme?: any[];
  horizont?: any;
  substrat_layers?: { kuerzel?: string; beschreibung?: string }[];
  substrat_kuerzel?: string[];
  hydromorphiehinweise?: string[];
  fussnoten?: any;
  klassifikation?: string;
  seitenzahl?: string;
  fbf_klassensuffix?: string;
  raw_fills?: Record<string, Record<string, string>>;
  [k: string]: any;
}

// Standard-Listen aus JSON-Dateien
import Forstämter from '../../assets/json/Forstämter.json';
import Humusformen from '../../assets/json/Humusformen.json';
import HorizontSymbole from '../../assets/json/HorizontSymbole.json';
import Bodenarten from '../../assets/json/Bodenarten.json';
import Wuchsgebiete from '../../assets/json/Wuchsgebiete.json';
import ReliefformUmgebung from '../../assets/json/ReliefformUmgebung.json';
import Nährkraftstufen from '../../assets/json/Nährkraftstufen.json';
import Substrattypengruppen from '../../assets/json/Substrattypengruppen.json';
import BodenformSuffixe from '../../assets/json/BodenformSuffixe.json';
import Torfarten from '../../assets/json/Torfarten.json';
import Farben from '../../assets/json/Farben.json';
import Klimastufen from '../../assets/json/Klimastufen.json';
import Mesoklima from '../../assets/json/Mesoklima.json';
import Feinbodenformen from '../../assets/json/Feinbodenformen.json';
import BodenartenTabelle from '../../assets/json/BodenartenTabelle.json';
// Default-Quelle des HBF-Katalogs: flacher D_Parser-Vollexport (397 Steckbriefe),
// generiert via tools/build-bodensteckbriefe.mjs. Viewer/Editor arbeiten direkt
// darauf; Editor-Aenderungen werden in PouchDB/LocalStorage DARUEBER gespeichert.
import Bodensteckbriefe from '../../assets/json/Bodensteckbriefe.json';
// Phase 3: bisher nur gebündelte (nicht editierbare) Listen — jetzt DB-gestützt + editierbar
import Punktarten from '../../assets/json/Punktarten.json';
import Hangteil from '../../assets/json/Hangteil.json';
import Neigungsstufe from '../../assets/json/Neigungsstufe.json';
import Woelbung from '../../assets/json/Woelbung.json';
import Hangrichtung from '../../assets/json/Hangrichtung.json';
import Hanganschluss from '../../assets/json/Hanganschluss.json';
import InnereRauhheit from '../../assets/json/InnereRauhheit.json';
import Bestandsbeschreibungen from '../../assets/json/Bestandsbeschreibungen.json';
import Wuchsbezirke from '../../assets/json/Wuchsbezirke.json';
import Reviere from '../../assets/json/Reviere.json';
import Bodenvegetationen from '../../assets/json/Bodenvegetationen.json';
// Phase 4a: ehemalige dietmarsMdb-Tabellen (bisher per HTTP via MdbService geladen)
import ZdaRelFo from '../../assets/tables/zda_RelFo.json';
import ZcaGeEinh from '../../assets/tables/zca_GeEinh.json';
import ZcbGeMor from '../../assets/tables/zcb_GeMor.json';
import ZcdSerie from '../../assets/tables/zcd_Serie.json';

/**
 * Qualitätsstufen lagen bisher nur inline im DocumentHandler (getHardData).
 * Hier als Seed der zentralen Liste — Form: { id:number, name:string }.
 */
const QUALITAETSSTUFEN = [
  { id: 1, name: 'Altformular / Umstufung' },
  { id: 2, name: 'unvollständige Kartierung' },
  { id: 3, name: 'Aktuelle Kartierung' },
];

@Injectable({
  providedIn: 'root'
})
export class ListenService extends ListStoreService {
  private injector = inject(Injector);
  private dbConfig = inject(DatabaseConfigService);
  private dbConnectionConfigs = inject(DB_CONNECTION_CONFIGS);

  static bodenartenTabelleToArray(): BodenartEntry[] {
    return Object.entries(BodenartenTabelle).map(([name, data]) => ({
      name,
      Lagerungsdichte: (data as any).Lagerungsdichte,
      Humusgehalt: (data as any).Humusgehalt
    }));
  }

  static bodenartenTabelleFromArray(entries: BodenartEntry[]): Record<string, { Lagerungsdichte: any; Humusgehalt: any }> {
    const obj: Record<string, any> = {};
    for (const e of entries) {
      obj[e.name] = { Lagerungsdichte: e.Lagerungsdichte, Humusgehalt: e.Humusgehalt };
    }
    return obj;
  }

  readonly editableLists: ListConfig[] = [
    // SEA95-Bodensteckbriefe (HBF-Katalog). Default = gebuendelter D_Parser-
    // Vollexport (assets/json/Bodensteckbriefe.json). Frisch/ohne gespeicherte
    // Liste liefert getList genau diese Quelle; der Import-Button bleibt fuer
    // spaetere Aktualisierungen aus einem neuen D_Parser-Export.
    { name: 'Bodensteckbriefe', displayName: 'Hauptbodenformenkatalog (SEA Teil D)', type: 'bodensteckbrief', defaultData: Bodensteckbriefe as any[] },
    { name: 'Feinbodenformen', displayName: 'Feinbodenformen', type: 'feinbodenform', defaultData: Feinbodenformen as any[] },
    { name: 'Forstämter', displayName: 'Forstämter', type: 'simple', defaultData: Forstämter },
    { name: 'Humusformen', displayName: 'Humusformen', type: 'simple', defaultData: Humusformen },
    { name: 'HorizontSymbole', displayName: 'Horizont-Symbole', type: 'simple', defaultData: HorizontSymbole },
    { name: 'Bodenarten', displayName: 'Bodenarten', type: 'simple', defaultData: Bodenarten },
    { name: 'BodenartenTabelle', displayName: 'Bodenarten nWSK-Eigenschaften', type: 'bodenartenTabelle', defaultData: ListenService.bodenartenTabelleToArray() },
    { name: 'Wuchsgebiete', displayName: 'Wuchsgebiete', type: 'simple', defaultData: Wuchsgebiete },
    { name: 'ReliefformUmgebung', displayName: 'Reliefform Umgebung', type: 'simple', defaultData: ReliefformUmgebung },
    { name: 'Nährkraftstufen', displayName: 'Nährkraftstufen', type: 'simple', defaultData: Nährkraftstufen },
    { name: 'Substrattypengruppen', displayName: 'Substrattypengruppen', type: 'simple', defaultData: Substrattypengruppen },
    { name: 'BodenformSuffixe', displayName: 'Bodenform-Suffixe', type: 'valueNamePair', defaultData: BodenformSuffixe },
    { name: 'Torfarten', displayName: 'Torfarten', type: 'valueNamePair', defaultData: Torfarten },
    { name: 'Farben', displayName: 'Farben', type: 'valueNamePair', defaultData: Farben },
    { name: 'Klimastufen', displayName: 'Klimastufen', type: 'simple', defaultData: Klimastufen },

    // --- Phase 3: bisher nur gebündelt, jetzt zentral in der DB + editierbar ---
    { name: 'Punktarten', displayName: 'Punktarten', type: 'genericTable', category: 'stammdaten', defaultData: Punktarten as any[] },
    { name: 'Hangteil', displayName: 'Hangteil', type: 'simple', category: 'stammdaten', defaultData: Hangteil },
    { name: 'Neigungsstufe', displayName: 'Neigungsstufe', type: 'genericTable', category: 'stammdaten', defaultData: Neigungsstufe as any[] },
    { name: 'Woelbung', displayName: 'Wölbung', type: 'genericTable', category: 'stammdaten', defaultData: Woelbung as any[] },
    { name: 'Hangrichtung', displayName: 'Hangrichtung', type: 'genericTable', category: 'stammdaten', defaultData: Hangrichtung as any[] },
    { name: 'Hanganschluss', displayName: 'Hanganschluss', type: 'genericTable', category: 'stammdaten', defaultData: Hanganschluss as any[] },
    { name: 'InnereRauhheit', displayName: 'Innere Rauhheit', type: 'genericTable', category: 'stammdaten', defaultData: InnereRauhheit as any[] },
    { name: 'Bestandsbeschreibungen', displayName: 'Bestandsbeschreibungen', type: 'genericTable', category: 'stammdaten', defaultData: Bestandsbeschreibungen as any[] },
    { name: 'Wuchsbezirke', displayName: 'Wuchsbezirke', type: 'genericTable', category: 'stammdaten', defaultData: Wuchsbezirke as any[] },
    { name: 'Qualitätsstufen', displayName: 'Qualitätsstufen', type: 'genericTable', category: 'stammdaten', defaultData: QUALITAETSSTUFEN },
    // Großkataloge: DB-gestützt, Such-/Render-begrenzt im generischen Editor, Massenpflege via Import
    { name: 'Reviere', displayName: 'Reviere', type: 'genericTable', category: 'katalog', defaultData: Reviere as any[] },
    { name: 'Bodenvegetationen', displayName: 'Pflanzenliste (GermanSL)', type: 'genericTable', category: 'katalog', defaultData: Bodenvegetationen as any[] },

    // --- Phase 4a: ehemalige dietmarsMdb-Tabellen (Geologie/Relief) — jetzt DB-gestützt + editierbar ---
    { name: 'zda_RelFo', displayName: 'Reliefformen (Katalog)', type: 'genericTable', category: 'geologie', defaultData: ZdaRelFo as any[] },
    { name: 'zca_GeEinh', displayName: 'Geologische Einheiten', type: 'genericTable', category: 'geologie', defaultData: ZcaGeEinh as any[] },
    { name: 'zcb_GeMor', displayName: 'Geomorphologie', type: 'genericTable', category: 'geologie', defaultData: ZcbGeMor as any[] },
    { name: 'zcd_Serie', displayName: 'KMgCaP-Serien', type: 'genericTable', category: 'geologie', defaultData: ZcdSerie as any[] },

    // --- Phase 4b: ehemals hartkodierte Inline-Dropdowns (echte Fachlisten) — jetzt editierbar.
    // Führendes '' = die bisherige "Leeren"-Option (führendes | im options-String). ---
    { name: 'OekologischerMoortyp', displayName: 'Ökologischer Moortyp', type: 'simple', category: 'inline', defaultData: ['', 'SA - Sauer-Armmoor', 'SZ - Sauer-Zwischenmoor', 'BZ - Basenzwischenmoor', 'KZ - Kalk-Zwischenmoor', 'RM - Reichmoor'] },
    { name: 'HydrogenetischerMoortyp', displayName: 'Hydrogenetischer Moortyp', type: 'simple', category: 'inline', defaultData: ['', 'Verlandungsmoor', 'Versumpfungsmoor', 'Kesselmoor', 'Durchströmungsmoor', 'Quellmoor', 'Hangmoor', 'Hangquellmoor', 'Überflutungsmoor', 'Regenmoor'] },
    { name: 'GefuegeForm', displayName: 'Gefügeform', type: 'simple', category: 'inline', defaultData: ['', 'pol', 'krü', 'pla', 'lam', 'Einzelkorn', 'Massiv', 'Verkittung', 'ungegliedert'] },
    // Mesoklima war bis 29.08.2026 an ZWEI Stellen fest verdrahtet (option.service
    // `options: "|fr|tr"` + inline im Naturraum-Template) und tauchte deshalb in
    // der Listenverwaltung gar nicht auf — es liess sich also kein Wert ergaenzen
    // (Nutzerin-Rueckmeldung: `lf` fehlte). Jetzt eine normale, pflegbare Liste.
    { name: 'Mesoklima', displayName: 'Mesoklima', type: 'simple', category: 'inline', defaultData: Mesoklima },
    { name: 'Klimabereich', displayName: 'Klimabereich', type: 'simple', category: 'inline', defaultData: ['', 'planar', 'collin', 'submontan', 'montan', 'hochmontan', 'subalpin'] },
    { name: 'Bestockungstyp', displayName: 'Bestockungstyp', type: 'simple', category: 'inline', defaultData: ['', 'Reinbestand', 'Mischbestand', 'Plenterwald', 'Lichtwald', 'Verjüngung', 'Bestockungslücke', 'Blöße'] },
    { name: 'Begruendungsart', displayName: 'Begründungsart', type: 'simple', category: 'inline', defaultData: ['', 'Naturverjüngung', 'Naturanflug', 'Saat', 'Pflanzung', 'Stockausschlag', 'Wurzelbrut'] },
    { name: 'Mischform', displayName: 'Mischform', type: 'simple', category: 'inline', defaultData: ['', 'Reinbestand', 'einzelstammweise', 'truppweise', 'gruppenweise', 'horstweise', 'flächig'] },
    { name: 'Waldfunktion', displayName: 'Waldfunktion', type: 'simple', category: 'inline', defaultData: ['', 'Wirtschaftswald', 'Schutzwald Boden', 'Schutzwald Wasser', 'Schutzwald Lawinen', 'Schutzwald Klima', 'Erholungswald', 'Naturschutz', 'Bannwald', 'Genressourcenwald'] },
    { name: 'ZustandsSTFG', displayName: 'Zustand Standortsformengruppe', type: 'simple', category: 'inline', defaultData: ['', 'naturnah', 'bedingt naturnah', 'kulturbetont', 'kulturbestimmt', 'naturfern'] },
    { name: 'BaumartArt', displayName: 'Baumarten', type: 'simple', category: 'inline', defaultData: ['', 'Fi', 'Ta', 'Dgl', 'Ki', 'Lae', 'Bu', 'Ei', 'REi', 'TEi', 'Hbu', 'Es', 'Ah', 'BAh', 'SAh', 'FAh', 'Ul', 'Bi', 'Erl', 'Pa', 'Vk', 'Wei', 'Li', 'Ro', 'Kie'] },
  ];

  private _currentUser: string | null = null;

  constructor() {
    super();
    // Lokale Replik öffnen + Hintergrund-Pull anstoßen (blockiert nicht).
    void this.ready();

    // Nach echtem Login die Remote-Verbindung NEU aufbauen: der erste Pull laeuft
    // im Pre-Token-Fenster (kc.authenticated noch false → url() = Public-Pfad, kein
    // Bearer). Sobald der Token da ist, liefert url() den authentifizierten Pfad und
    // newDb haengt den Bearer an → sauberer Pull statt 401/405. AuthService lazy
    // (DI-Zyklus vermeiden, wie in saveList).
    void import('../auth.service').then(({ AuthService }) => {
      const auth = this.injector.get(AuthService);
      auth.loggedIn.subscribe(() => void this.refreshFromRemote());
      if (auth.authReady()) void this.refreshFromRemote();
    });

    window.addEventListener('online', () => {
      void this.refreshFromRemote();
    });
    window.addEventListener('offline', () => {
      this.storageType$.next('local');
    });
  }

  /**
   * Bearer pro Request, damit der Listen-Store am Gateway authentifiziert
   * repliziert statt anonym (401/Public). Lokale DBs (Name statt URL) bleiben
   * unveraendert — das erledigt die Store-Basis.
   */
  protected override authHeaders(): Record<string, string> | null | undefined {
    return this.dbConnectionConfigs[0]?.getAuthHeaders?.() ?? null;
  }

  /**
   * Abgelaufener Token: einmal still erneuern, dann wiederholt der fetch-Wrapper
   * die Anfrage. Sonst steht die Replikation bis zum naechsten Neuladen.
   */
  protected override authRefresh(): Promise<boolean> {
    return this.dbConnectionConfigs[0]?.refreshAuth?.() ?? Promise.resolve(false);
  }

  /**
   * Auf dem Nur-Lese-Weg festgefahren: zurueck auf die richtige Adresse.
   *
   * Die Verbindung waehlt ihren Weg nur beim Aufbau. Startet die App ohne
   * greifbaren Token (Standby, wackliges Netz), bleibt sie auf `/db/public/...`
   * stehen — Ansehen geht, Speichern bekommt ein 405, und das fuer die ganze
   * Sitzung (Nutzer-Befund 01.09.2026).
   *
   * Den Pull beruehrt das nicht: `replicateFromRemote` schaltet die Checkpoints
   * ab (`checkpoint: false`), es geht also gar kein PUT an die Quelle. Hier
   * wirkt es fuer das Schreiben der Listen selbst.
   */
  protected override adressReparatur(url: string): string | undefined {
    return this.dbConnectionConfigs[0]?.repariereAdresse?.(url);
  }

  /**
   * Pull OHNE `_local`-Checkpoints (`checkpoint: false`):
   *  - die Remote-Quelle ist ausgeloggt/read-only nur GET-bar → ein Source-Checkpoint-
   *    PUT gaebe 405;
   *  - mit `checkpoint:'target'` bliebe der Source-Checkpoint ungeschrieben → jede
   *    Replikation probt ihn erneut mit einem (in der Konsole sichtbaren) 404.
   * Die Listen-DB ist klein → ein voller Pull pro Refresh ist vertretbar, dafuer bleibt
   * die Konsole still. Versions-Schnappschuesse (`listhist_`) bleiben im Remote-Master.
   */
  protected override async replicateFromRemote(remote: any, local: any): Promise<void> {
    await local.replicate.from(remote, {
      checkpoint: false,
      filter: (doc: any) => !String(doc?._id ?? '').startsWith('listhist_'),
    });
  }

  // ===== ListStoreService-Konfiguration =====

  /** Remote-Master = dedizierte Listen-DB `bpweb-listen` (nur online). */
  protected get remoteDbUrl(): string | null {
    if (!navigator.onLine) return null;
    const conn = this.dbConnectionConfigs[0];
    return conn ? conn.url(this.dbConfig.listenDbName) : null;
  }

  /** Lokale Replik (konstanter Name, abgeleitet aus dem zentralen DB-Namen). */
  protected get localCacheDbName(): string {
    return `${this.dbConfig.listenDbName}-cache`;
  }

  protected getCurrentUser(): string {
    return this._currentUser || 'Unbekannt';
  }

  /**
   * Punktarten-Liste IMMER in Objekt-Form liefern — auch wenn in der DB noch die
   * historische String-Liste ("<nummer>_<beschreibung>") liegt (vor der Umstellung
   * auf Spalten). So zeigen Editor/Typeahead/Legende sofort Werte; beim naechsten
   * Speichern wird die Objekt-Form persistiert. Alle Leser (getList/getHardcoded/
   * applyStoreDerived) gehen ueber getSync -> ein Chokepoint.
   */
  override getSync(listName: string): any[] {
    const data = super.getSync(listName);
    if (listName === 'Punktarten') return normalizePunktartList(data);
    if (listName === 'Feinbodenformen') return this.composeFeinbodenformen(data);
    // Lehmbodenform-Schraffuren wurden teils als Array der schraffierten
    // Spalten-IDs gespeichert (statt Map) -> Renderer leckte Array-Elemente als
    // Zahlen in die Schraffurtabelle. Hier auf die Haken-Map normalisieren.
    if (listName === 'Bodensteckbriefe') return normalizeBodensteckbriefe(data);
    return data;
  }

  /**
   * Feinbodenformen-Liste live aus dem Hauptbodenformenkatalog ableiten:
   * Kernfelder (Name, Code, HBF, Naehrkraft, Seite, Serie) kommen aus dem
   * Katalog, die eigenen Zusatzinfos (zuordnung, Hw) aus dem gespeicherten
   * Dokument. Memoisiert ueber Referenz-Identitaet (Katalog + Stored), damit der
   * synchrone Chokepoint nicht bei jedem Aufruf neu komponiert.
   */
  private _fbfMemo: { catalog: any[]; stored: any[]; result: any[] } | null = null;
  private composeFeinbodenformen(stored: any[]): any[] {
    const catalog = super.getSync('Bodensteckbriefe');
    const memo = this._fbfMemo;
    if (memo && memo.catalog === catalog && memo.stored === stored) return memo.result;
    const result = deriveFeinbodenformen(catalog as any[], stored as any[]).list;
    this._fbfMemo = { catalog, stored, result };
    return result;
  }

  /** Portal-Uebersicht: fuer Feinbodenformen die KOMPONIERTE Anzahl melden
   *  (Katalog-FBFs + Waisen), nicht den schlanken Speicher-Stand. */
  override async getListsOverview(): Promise<ListOverviewEntry[]> {
    const overview = await super.getListsOverview();
    return overview.map(o =>
      o.name === 'Feinbodenformen' ? { ...o, count: this.getSync('Feinbodenformen').length } : o,
    );
  }

  /** Override saveList um AuthService lazy zu laden (currentUser für updatedBy). */
  override async saveList(listName: string, data: any[]): Promise<void> {
    const { AuthService } = await import('../auth.service');
    const authService = this.injector.get(AuthService);
    this._currentUser = authService.currentUser || 'Unbekannt';
    // Feinbodenformen: nur die eigenen Zusatzinfos (+ manuelle Waisen) ablegen;
    // die abgeleiteten Felder kommen beim Lesen live aus dem Katalog.
    if (listName === 'Feinbodenformen') {
      data = toStoredFeinbodenformen(data, super.getSync('Bodensteckbriefe') as any[]);
    }
    // Komposition invalidieren, wenn sich Katalog ODER FBF-Extras aendern.
    if (listName === 'Bodensteckbriefe' || listName === 'Feinbodenformen') {
      this._fbfMemo = null;
    }
    return super.saveList(listName, data);
  }

  /**
   * Einmal-Migration der Listen aus der Punkte-DB (`bpweb-punkte`) in die neue
   * Listen-DB (`bpweb-listen`). Wird vom Admin-Button in der Listenverwaltung
   * ausgelöst. Idempotent (Marker), Punkte-DB nur lesend. Danach Store-Refresh.
   */
  async runListMigration(): Promise<ListMigrationResult & { seeded: string[] }> {
    // Benutzer für updatedBy laden (wie in saveList).
    const { AuthService } = await import('../auth.service');
    this._currentUser = this.injector.get(AuthService).currentUser || 'System';

    const conn = this.dbConnectionConfigs[0];
    if (!conn) throw new Error('Keine DB-Verbindung konfiguriert.');
    const source = new PouchDB(conn.url(this.dbConfig.punkteDbName), { skip_setup: true });
    const target = new PouchDB(conn.url(this.dbConfig.listenDbName), { skip_setup: false });

    // 1) Vorhandene list_*-Dokumente aus der Punkte-DB übernehmen.
    const result = await migrateListsToListenDb(source, target);
    // 2) Erstbefüllung: alle fehlenden Standardlisten als Defaults in bpweb-listen anlegen.
    const seed = await this.seedDefaultsToRemote();
    await this.refreshFromRemote();
    return { ...result, seeded: seed.seeded };
  }

  /**
   * Prüft (online), ob die Einmal-Migration bereits gelaufen ist (Marker in
   * bpweb-listen). Dient dem Ausblenden des Migrations-Buttons. Offline / DB
   * nicht erreichbar / Marker fehlt -> false (Button bleibt sichtbar).
   */
  async isListMigrationDone(): Promise<boolean> {
    if (!navigator.onLine) return false;
    const conn = this.dbConnectionConfigs[0];
    if (!conn) return false;
    try {
      const target = new PouchDB(conn.url(this.dbConfig.listenDbName), { skip_setup: true });
      const marker = await target.get(LIST_MIGRATION_MARKER).catch(() => null);
      return !!marker;
    } catch {
      return false;
    }
  }
}
